import React from 'react';
import SEO from '../../components/SEO';
import SiteFooter from '../../components/SiteFooter';
import DirectAnswer from '../../components/DirectAnswer';
import { Link } from 'react-router-dom';
import { emergencyDentistActonPageSEO } from '../../data/seo';
import { 
  getMedicalConditionLD, 
  getMedicalProcedureLD, 
  getBreadcrumbLD,
  getFAQPageLD 
} from '../../data/structuredData';
import { emergencyCondition, emergencyProcedure } from '../../data/medicalSchemas';
import { emergencyDentistryFAQs } from '../../data/faqs';
import { emergencyDentistBreadcrumbs } from '../../data/breadcrumbs';
import { Button } from '../../components/ui/button';
import MiniConsultButton from '../../components/MiniConsultButton';
import EnquireButton from '../../components/EnquireButton';
import { Phone, Smile, CheckCircle2, Clock, MapPin, Instagram, Facebook, Twitter, Music2, AlertCircle } from 'lucide-react';
import WhatsAppIcon from '@/components/WhatsAppIcon';

const EmergencyDentistActon = () => {
  const phoneNumber = '07787814064';
  const whatsappNumber = '447787814064';
  const phoneLink = `tel:${phoneNumber}`;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const whatsappLink = isMobile
    ? `whatsapp://send?phone=${whatsappNumber}&text=URGENT: I need emergency dental treatment`
    : `https://web.whatsapp.com/send?phone=${whatsappNumber}&text=URGENT: I need emergency dental treatment`;

  return (
    <>
      {/* SEO Meta Tags & Structured Data */}
      <SEO
        title={emergencyDentistActonPageSEO.title}
        description={emergencyDentistActonPageSEO.description}
        path={emergencyDentistActonPageSEO.path}
        keywords={emergencyDentistActonPageSEO.keywords}
        image={emergencyDentistActonPageSEO.image}
        structuredData={[
          getMedicalConditionLD(emergencyCondition),
          getMedicalProcedureLD(emergencyProcedure),
          getBreadcrumbLD(emergencyDentistBreadcrumbs),
          getFAQPageLD(emergencyDentistryFAQs),
        ]}
      />

      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-sky-gray/20 shadow-sm">
        <div className="bg-gradient-to-r from-burnt-coral/5 to-sky-gray/10 border-b border-sky-gray/20">
          <div className="container mx-auto px-4 py-2">
            <div className="flex items-center justify-between">
              <a href={phoneLink} className="flex items-center gap-2 text-sm md:text-base text-rabbit hover:text-burnt-coral transition-colors group">
                <Phone className="w-4 h-4 md:w-5 md:h-5 text-burnt-coral group-hover:scale-110 transition-transform" />
                <span className="font-semibold">07787 814 064</span>
              </a>
              <div className="flex items-center gap-3 md:gap-4">
                <a href="https://www.instagram.com/kartikpateldentist/" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on Instagram"><Instagram className="w-5 h-5" /></a>
                <a href="https://www.facebook.com/profile.php?id=61588647254315" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on Facebook"><Facebook className="w-5 h-5" /></a>
                <a href="https://twitter.com/restore_dental" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on X (Twitter)"><Twitter className="w-5 h-5" /></a>
                <a href="https://www.tiktok.com/@restore.dental" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on TikTok"><Music2 className="w-5 h-5" /></a>
              </div>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Smile className="w-8 h-8 text-burnt-coral" />
            <span className="text-xl md:text-2xl font-bold text-rabbit">restore.dental</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/"><Button variant="outline" size="sm" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white">Home</Button></Link>
            <Button size="sm" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" asChild><a href={phoneLink}>Call Now</a></Button>
          </div>
        </div>
      </header>

      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 bg-gradient-to-br from-burnt-coral/10 via-white to-[#006A71]/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-2 bg-burnt-coral/10 rounded-full border-2 border-burnt-coral/30">
              <p className="text-sm font-medium text-burnt-coral"><AlertCircle className="w-4 h-4 inline mr-1" />Emergency Dental Care Available</p>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-rabbit mb-6">Emergency Dentist in <span className="text-burnt-coral">Acton</span></h1>
            <p className="text-xl md:text-2xl text-chocolate-malt mb-8">Same-day appointments • Fast pain relief • Expert emergency care</p>
            <DirectAnswer
              question="Can I see an emergency dentist in Acton today?"
              answer="Yes — Restore.Dental at 64a Horn Lane, Acton W3 6NP offers same-day emergency appointments Monday to Saturday. Call or WhatsApp 07787 814 064. An emergency exam with X-rays costs £80–£120, temporary fillings or crowns £100–£150 and extractions £150–£300. We treat severe toothache, broken or knocked-out teeth, abscesses, lost crowns and bleeding."
              bullets={['Same-day slots Mon–Sat', 'Emergency exam £80–£120', '4 min from Acton Main Line', 'Call or WhatsApp 07787 814 064']}
              className="max-w-3xl mx-auto mb-8"
            />
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Button size="lg" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" asChild><a href={phoneLink}><Phone className="w-5 h-5 mr-2" />Call 07787 814 064</a></Button>
              <Button size="lg" variant="outline" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" asChild><a href={whatsappLink}><WhatsAppIcon className="w-5 h-5 mr-2" />WhatsApp Now</a></Button>
              <EnquireButton slug="emergency" about="Emergency Appointment" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" testId="hero-enquire-btn" />
            </div>
            <div className="bg-burnt-coral/10 border-2 border-burnt-coral/30 rounded-xl p-4 inline-block">
              <p className="text-burnt-coral font-bold">🚨 IN PAIN? We'll do our best to see you TODAY</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-6">We Treat Dental Emergencies in Acton</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl">
                  <AlertCircle className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Severe Toothache</h3>
                  <p className="text-chocolate-malt">Fast pain relief and treatment to address the root cause</p>
                </div>
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl">
                  <AlertCircle className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Broken or Chipped Tooth</h3>
                  <p className="text-chocolate-malt">Repair or restore your tooth the same day</p>
                </div>
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl">
                  <AlertCircle className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Knocked-Out Tooth</h3>
                  <p className="text-chocolate-malt">Time is critical - call us immediately</p>
                </div>
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl">
                  <AlertCircle className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Dental Abscess</h3>
                  <p className="text-chocolate-malt">Urgent treatment to prevent infection spread</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-burnt-coral/10 via-sky-gray/10 to-[#D4AF37]/10 rounded-2xl p-8 md:p-12 text-center border-2 border-burnt-coral">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-4">Need Urgent Dental Care?</h2>
              <p className="text-lg text-chocolate-malt mb-6">Call us now. We reserve appointment slots specifically for emergencies.</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button size="lg" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" asChild><a href={phoneLink}><Phone className="w-5 h-5 mr-2" />Call 07787 814 064</a></Button>
                <Button size="lg" variant="outline" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" asChild><a href={whatsappLink}><WhatsAppIcon className="w-5 h-5 mr-2" />WhatsApp</a></Button>
                <EnquireButton slug="emergency" about="Emergency Appointment" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" testId="cta-enquire-btn" />
              </div>
            </div>

            <div className="mt-16">
              <h2 className="text-2xl font-bold text-rabbit mb-6 text-center">Our Other Services</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/dentist-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all"><h3 className="font-bold text-rabbit mb-2">General Dentistry</h3><p className="text-sm text-chocolate-malt">Routine check-ups & care</p></Link>
                <Link to="/iv-sedation-dental-phobia" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all" data-testid="emergency-sedation-link"><h3 className="font-bold text-rabbit mb-2">Too Frightened to Come In?</h3><p className="text-sm text-chocolate-malt">Treatment under IV sedation</p></Link>
                <Link to="/invisalign-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all"><h3 className="font-bold text-rabbit mb-2">Invisalign</h3><p className="text-sm text-chocolate-malt">Clear aligners</p></Link>
                <Link to="/composite-bonding-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all"><h3 className="font-bold text-rabbit mb-2">Composite Bonding</h3><p className="text-sm text-chocolate-malt">Same-day smile fix</p></Link>
                <Link to="/cosmetic-dentist-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all"><h3 className="font-bold text-rabbit mb-2">Cosmetic Dentistry</h3><p className="text-sm text-chocolate-malt">Complete makeovers</p></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />

      {/* Mini Consult Button */}
      <MiniConsultButton />
    </>
  );
};

export default EmergencyDentistActon;